// Vanilla dropdown, in place of the Fomantic module the app used to load with jQuery.
//
// It keeps Fomantic's markup contract, so the stylesheet (stylesheets/lib/_dropdown.scss)
// and the system tests read the same DOM as before: a <select> is replaced by a
// `.selection.dropdown` wrapper carrying its classes, with the select hidden inside, a
// `.dropdown.icon`, an `input.search` for the `search` variant, a `.text` and a
// `.menu > .item[data-value]`. The state classes are Fomantic's too: `visible` on the open
// menu, `active` on the selected item, `selected` on the highlighted one, `filtered` on
// the items a search hides, `default` on a placeholder text.
//
// A change of value dispatches a native, bubbling `change` on the <select> or the hidden
// input, then calls `settings.onChange(value, text, item)`. From a script, set the value of
// the select (or hidden input) and dispatch `change` on it: the dropdown follows.

export type DropdownAction = 'activate' | 'hide' | 'nothing'

export interface DropdownApiSettings {
  url: string
  className?: { loading?: string }
}

export interface DropdownMessages {
  addResult: string
  maxSelections: string
  noResults: string
}

// Called with the module (the `.dropdown` wrapper) as `this`.
export type DropdownChangeHandler = (
  this: HTMLElement,
  value: string,
  text: string | null,
  item: HTMLElement | null | undefined
) => void

export interface DropdownSettings {
  action: DropdownAction
  allowAdditions: boolean
  hideAdditions: boolean
  apiSettings: DropdownApiSettings | null
  forceSelection: boolean
  keepSearchTerm: boolean
  placeholder: 'auto' | false
  preserveHTML: boolean
  direction: 'auto' | 'upward' | 'downward'
  maxSelections: number | false | null
  hideDividers: boolean | 'empty'
  showOnFocus: boolean
  onChange: DropdownChangeHandler
  onShow: (this: HTMLElement) => void
  onHide: (this: HTMLElement) => void
  message: DropdownMessages
}

// What the constructor takes: any of the settings, and any of the messages.
export type DropdownOptions = Partial<Omit<DropdownSettings, 'message'>> & { message?: Partial<DropdownMessages> }

// A result of the remote search.
export interface DropdownResult {
  type?: string
  name?: string
  value?: string
  text?: string
  disabled?: boolean
  edit_url?: string
}

// eslint-disable-next-line no-use-before-define -- a type only: the class is declared below
const instances = new WeakMap<Element, Dropdown>()

// Never from a zero opacity: the menu is on screen, and clickable, from its first frame.
const OPENING: Keyframe[] = [
  { opacity: 0.4, transform: 'translateY(-2px)' },
  { opacity: 1, transform: 'none' }
]

const DEFAULTS: Omit<DropdownSettings, 'message'> = {
  action: 'activate', // 'activate' selects the item, 'hide' only closes, 'nothing' keeps the menu open
  allowAdditions: false,
  hideAdditions: true,
  apiSettings: null, // { url: '/search?q={query}', className: { loading: 'loading-silent' } }
  forceSelection: false,
  keepSearchTerm: false,
  placeholder: 'auto',
  preserveHTML: false,
  direction: 'auto',
  maxSelections: false,
  hideDividers: false,
  showOnFocus: true,
  onChange() {},
  onShow() {},
  onHide() {}
}

const MESSAGES: DropdownMessages = {
  addResult: 'Ajouter <b>{term}</b>',
  maxSelections: '{maxCount} sélections maximum',
  noResults: 'Aucun résultat trouvé'
}

export function normalizeText(text: string | null | undefined) {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

export function escapeHTML(text: string | number | null | undefined) {
  const element = document.createElement('div')
  element.textContent = text as string | null
  return element.innerHTML
}

export default class Dropdown {
  declare settings: DropdownSettings
  declare select: HTMLSelectElement | null
  declare module: HTMLElement
  declare input: HTMLSelectElement | HTMLInputElement | null
  declare menu: HTMLElement
  declare text: HTMLElement | null
  declare isSelection: boolean
  declare isMultiple: boolean
  declare search?: HTMLInputElement
  declare defaultText: string | null
  declare placeholderText?: string | null
  declare keyboardChosen?: boolean
  declare dispatchingChange?: boolean
  declare mouseDown?: boolean
  declare menuMouseDown?: boolean
  declare remoteTimer?: ReturnType<typeof setTimeout>
  declare lastRequest?: number
  declare documentClick?: (event: MouseEvent) => void
  declare documentMouseUp?: () => void
  // Set by the recipient-select controller once its onChange is in place.
  declare changeEventConfigured?: boolean

  // The instance of the dropdown holding `element`: the <select> it was built from, the
  // wrapper itself or anything inside it. The `.dropdown.icon` is not a dropdown.
  static for(element: Element | null | undefined) {
    return element ? instances.get(element.closest?.('.dropdown:not(.icon)') as Element) : undefined
  }

  constructor(element: HTMLElement, settings: DropdownOptions = {}) {
    this.settings = { ...DEFAULTS, ...settings, message: { ...MESSAGES, ...settings.message } }

    if (element.tagName === 'SELECT') {
      this.select = element as HTMLSelectElement
      this.module = this.wrapSelect(element as HTMLSelectElement)
    } else {
      this.module = element
      this.select = element.querySelector<HTMLSelectElement>(':scope > select')
    }
    this.input = this.select || this.module.querySelector<HTMLInputElement>(':scope > input[type="hidden"]')
    this.menu = this.module.querySelector<HTMLElement>(':scope > .menu')!
    this.text = this.module.querySelector<HTMLElement>(':scope > .text')
    this.isSelection = this.module.classList.contains('selection')
    this.isMultiple = this.module.classList.contains('multiple') || !!this.select?.multiple
    if (!this.menu) return

    instances.set(this.module, this)
    this.setupSearch()
    this.setupTabbable()
    if (this.select) this.renderOptions()
    this.defaultText = this.text?.classList.contains('default') ? this.text.innerHTML : null
    // A Turbo snapshot can hold a dropdown captured open.
    this.module.classList.remove('active', 'visible')
    this.menu.classList.remove('visible')
    this.sync()
    this.bind()
  }

  // Public API ---------------------------------------------------------------------------

  get value() {
    return this.values().join(',')
  }

  values(): string[] {
    if (!this.input) return []
    if (this.select?.multiple) return Array.from(this.select.selectedOptions, option => option.value)
    if (this.isMultiple) return this.input.value.split(',').filter(Boolean)

    return [this.input.value]
  }

  setting<Name extends keyof DropdownSettings>(name: Name, value: DropdownSettings[Name]) {
    this.settings[name] = value
  }

  items() {
    return Array.from(this.menu.querySelectorAll<HTMLElement>('.item'))
  }

  item(value: string | number) {
    return this.items().find(item => this.itemValue(item) === String(value))
  }

  itemValue(item: HTMLElement) {
    return item.dataset.value ?? item.textContent.trim()
  }

  itemText(item: HTMLElement) {
    if (item.dataset.text !== undefined) return item.dataset.text

    return this.settings.preserveHTML ? item.innerHTML.trim() : item.textContent.trim()
  }

  // Selects `value`, from the menu or, for a value the menu does not offer (an addition, a
  // search result), with `text` as its label: without one, a <select> labels it with its own
  // option, and a value alone never replaces the label on screen. A multiple dropdown adds it
  // to its values.
  setSelected(value: string | number | null | undefined, text: string | null = null) {
    value = String(value ?? '')
    const item = this.item(value)
    text ??= item ? this.itemText(item) : null

    if (this.isMultiple) {
      this.addValue(value, text, item)
    } else {
      this.setValue(value, text, item)
    }
  }

  clear() {
    if (this.isMultiple) {
      this.writeValues([])
    } else {
      this.writeValue('')
    }
    this.sync()
    this.notifyChange('', '', null)
  }

  setText(text: string) {
    if (!this.text) return

    if (this.settings.preserveHTML) {
      this.text.innerHTML = text
    } else {
      this.text.textContent = text
    }
    this.text.classList.remove('default')
  }

  // Re-reads the menu from the <select> options, and the state from the input value.
  refresh() {
    if (this.select) this.renderOptions()
    this.sync()
  }

  isVisible() {
    return this.menu.classList.contains('visible')
  }

  isDisabled() {
    return this.module.classList.contains('disabled') || !!this.select?.disabled
  }

  toggle() {
    this.isVisible() ? this.hide() : this.show()
  }

  show({ query = true }: { query?: boolean } = {}) {
    if (this.isVisible() || this.isDisabled()) return

    this.menu.classList.remove('hidden')
    this.menu.classList.add('visible')
    this.module.classList.add('active', 'visible')
    if (this.settings.direction === 'upward') this.module.classList.add('upward')
    this.bindDocument()

    // A menu of links behind a button is shown as it is.
    if (query && this.settings.apiSettings) {
      this.queryRemote(this.search?.value || '')
    } else if (query && (this.isSelection || this.search)) {
      this.filter(this.search?.value || '')
    }
    if (!this.isVisible()) return

    this.menu.animate?.(OPENING, { duration: 120, easing: 'ease-out' })
    this.settings.onShow.call(this.module)
  }

  hide({ clearSearch = true }: { clearSearch?: boolean } = {}) {
    if (!this.isVisible()) return

    this.keyboardChosen = false
    this.menu.classList.remove('visible')
    this.menu.classList.add('hidden')
    this.module.classList.remove('active', 'visible')
    this.unbindDocument()
    if (clearSearch && this.search && !this.settings.keepSearchTerm) this.clearSearch()
    this.settings.onHide.call(this.module)
  }

  // Setup -------------------------------------------------------------------------------

  // Like Fomantic: the classes of the <select> move onto the wrapper, the select hides inside
  // it, and a `required` select turns into a forced selection (a hidden control cannot be
  // validated by the browser). A wrapper restored from a Turbo snapshot is picked up again.
  wrapSelect(select: HTMLSelectElement) {
    if (select.parentElement!.matches('.dropdown')) return select.parentElement!

    const module = document.createElement('div')
    module.className = select.className
    module.classList.add('selection', 'dropdown')
    if (select.multiple) module.classList.add('multiple')
    if (select.disabled) module.classList.add('disabled')
    if (select.required) {
      this.settings.forceSelection = true
      select.removeAttribute('required')
    }
    select.removeAttribute('class')
    select.before(module)
    module.append(select)
    module.insertAdjacentHTML('beforeend', '<i class="dropdown icon"></i><div class="text"></div><div class="menu"></div>')

    return module
  }

  setupSearch() {
    if (!this.module.classList.contains('search')) return

    this.search = this.module.querySelector(':scope > input.search') as HTMLInputElement | undefined
    if (this.search) return

    this.search = document.createElement('input')
    this.search.className = 'search'
    this.search.autocomplete = 'off'
    ;(this.text || this.menu).before(this.search)
  }

  // The system tests wait on the menu tabindex to know the dropdown is set up.
  setupTabbable() {
    this.menu.tabIndex = -1
    if (this.search) {
      this.search.tabIndex = 0
    } else if (this.isSelection && !this.module.hasAttribute('tabindex')) {
      this.module.tabIndex = 0
    }
  }

  // The menu mirrors the <select>: an empty option is the placeholder (unless the
  // placeholder setting is false), an <optgroup> becomes a header.
  renderOptions() {
    const fragment = document.createDocumentFragment()
    this.placeholderText = null

    const addOption = (option: HTMLOptionElement) => {
      const text = option.textContent.trim()
      if (option.value === '' && this.settings.placeholder === 'auto') {
        this.placeholderText ??= text
        return
      }
      const item = document.createElement('div')
      item.className = option.disabled ? 'disabled item' : 'item'
      item.dataset.value = option.value
      item.dataset.text = text
      item.textContent = text
      fragment.append(item)
    }

    for (const child of this.select!.children) {
      if (child.tagName === 'OPTGROUP') {
        const header = document.createElement('div')
        header.className = 'header'
        header.textContent = (child as HTMLOptGroupElement).label
        fragment.append(header)
        Array.from(child.children as HTMLCollectionOf<HTMLOptionElement>).forEach(addOption)
      } else {
        addOption(child as HTMLOptionElement)
      }
    }
    // Not replaceChildren(): .browserslistrc still claims Safari 13, which lacks it (Safari 14).
    this.menu.innerHTML = ''
    this.menu.append(fragment)
  }

  // State -------------------------------------------------------------------------------

  // Brings the text, the labels and the item classes in line with the input value.
  sync() {
    const values = this.values().filter(value => value !== '' || this.settings.placeholder === false)

    this.items().forEach(item => {
      const active = values.includes(this.itemValue(item))
      item.classList.toggle('active', active)
      item.classList.toggle('selected', active && !this.isMultiple)
    })

    if (this.isMultiple) {
      this.renderLabels(values)
      return
    }

    const value = values[0]
    const item = value === undefined ? null : this.item(value)
    const optionText = this.optionText(value)
    if (item) {
      this.setText(this.itemText(item))
    } else if (optionText !== undefined) {
      this.setText(optionText)
    } else if (value && (!this.text || this.text.classList.contains('default') || !this.text.textContent)) {
      // A value the menu no longer lists, a search result for instance, keeps its label.
      this.setText(value)
    } else if (!value) {
      this.showPlaceholder()
    }
  }

  showPlaceholder() {
    if (!this.text) return

    if (this.placeholderText !== null && this.placeholderText !== undefined) {
      this.text.textContent = this.placeholderText
    } else if (this.defaultText !== null) {
      this.text.innerHTML = this.defaultText
    } else {
      this.text.textContent = ''
      this.text.classList.remove('default')
      return
    }
    this.text.classList.add('default')
  }

  optionText(value: string | undefined) {
    if (!this.select || !value) return undefined

    return Array.from(this.select.options)
      .find(option => option.value === value)
      ?.textContent.trim()
  }

  renderLabels(values: string[]) {
    this.module.querySelectorAll(':scope > a.label').forEach(label => label.remove())
    const anchor = this.search || this.text || this.menu

    values.forEach(value => {
      const item = this.item(value)
      const text = item ? this.itemText(item) : this.optionText(value) ?? value
      const label = document.createElement('a')
      label.className = 'label label-default'
      label.dataset.value = value
      label.innerHTML = `${this.settings.preserveHTML ? text : escapeHTML(text)}<i class="delete icon"></i>`
      anchor.before(label)
    })
  }

  setValue(value: string, text: string | null, item: HTMLElement | undefined) {
    if (value === this.values()[0]) {
      this.sync()
      return
    }

    this.writeValue(value, text)
    this.sync()
    if (text && !item) this.setText(text)
    this.notifyChange(value, text, item)
  }

  addValue(value: string, text: string | null, item: HTMLElement | undefined) {
    const values = this.values()
    if (!value || values.includes(value) || this.maxReached(values)) return

    this.writeValues([...values, value], text)
    this.sync()
    this.notifyChange(this.value, text, item)
  }

  removeValue(value: string) {
    this.writeValues(this.values().filter(current => current !== value))
    this.sync()
    this.notifyChange(this.value, '', null)
    if (this.isVisible()) this.filter(this.search?.value || '')
  }

  writeValue(value: string, text: string | null = value) {
    if (this.select) {
      if (value !== '') this.ensureOption(value, text)
      this.select.value = value
    } else if (this.input) {
      this.input.value = value
    }
  }

  writeValues(values: string[], text: string | null = null) {
    if (this.select) {
      values.forEach(value => this.ensureOption(value, value === values.slice(-1)[0] && text ? text : value))
      Array.from(this.select.options).forEach(option => {
        option.selected = values.includes(option.value)
      })
    } else if (this.input) {
      this.input.value = values.join(',')
    }
  }

  // A value the <select> does not offer (an addition, a search result) gets its option,
  // otherwise the select would silently drop it.
  ensureOption(value: string, text: string | null) {
    const existing = Array.from(this.select!.options).find(option => option.value === value)
    if (existing) return

    const option = new Option(this.plainText(text || value), value)
    option.className = 'addition'
    this.select!.append(option)
    this.renderOptions()
  }

  plainText(html: string) {
    if (!this.settings.preserveHTML) return html

    const element = document.createElement('div')
    element.innerHTML = html
    return element.textContent.trim()
  }

  notifyChange(value: string, text: string | null, item: HTMLElement | null | undefined) {
    this.dispatchingChange = true
    try {
      this.input?.dispatchEvent(new Event('change', { bubbles: true }))
    } finally {
      this.dispatchingChange = false
    }
    this.settings.onChange.call(this.module, value, text, item)
  }

  maxReached(values = this.values()) {
    return this.isMultiple && this.settings.maxSelections && values.length >= this.settings.maxSelections
  }

  // Search ------------------------------------------------------------------------------

  clearSearch() {
    this.search!.value = ''
    this.text?.classList.remove('filtered')
    this.menu.querySelector(':scope > .item.addition')?.remove()
  }

  // Local search: a case- and accent-insensitive substring of the item text, like
  // Fomantic's `fullTextSearch: 'exact'` with ignoreDiacritics and ignoreSearchCase.
  filter(term: string) {
    const query = normalizeText(term)
    const maxReached = this.maxReached()

    this.items().forEach(item => {
      if (item.classList.contains('addition')) return

      const hidden =
        maxReached ||
        (this.isMultiple && item.classList.contains('active')) ||
        (query !== '' && !normalizeText(item.textContent).includes(query))
      item.classList.toggle('filtered', hidden)
    })

    this.renderAddition(term.trim(), maxReached)
    this.hideEmptyDividers()
    this.renderMessage(term.trim(), maxReached)

    const selectable = this.selectableItems()
    this.highlight(query ? selectable[0] : selectable.find(item => item.classList.contains('active')) || selectable[0])
  }

  renderAddition(term: string, maxReached: boolean | number | null) {
    let addition = this.menu.querySelector<HTMLElement>(':scope > .item.addition')
    const exists = this.items().some(
      item => item !== addition && normalizeText(this.itemValue(item)) === normalizeText(term)
    )

    if (!this.settings.allowAdditions || !term || exists || maxReached) {
      addition?.remove()
      return
    }

    if (!addition) {
      addition = document.createElement('div')
      addition.className = 'addition item'
      this.menu.prepend(addition)
    }
    addition.dataset.value = term
    addition.dataset.text = term
    addition.innerHTML = this.settings.message.addResult.replace('{term}', escapeHTML(term))
    // `filtered` does the hiding: the stylesheet displays every menu item over `hidden`.
    addition.classList.toggle('hidden', this.settings.hideAdditions)
    addition.classList.toggle('filtered', this.settings.hideAdditions)
  }

  hideEmptyDividers() {
    if (this.settings.hideDividers !== 'empty') return

    let separator: Element | null = null
    let visibleUnder = false
    const close = () => separator?.classList.toggle('hidden', !visibleUnder)

    Array.from(this.menu.children).forEach(child => {
      if (child.matches('.header, .divider')) {
        close()
        separator = child
        visibleUnder = false
      } else if (child.matches('.item:not(.filtered, .hidden)')) {
        visibleUnder = true
      }
    })
    close()
  }

  // An empty menu is never shown: a search without a match shows the "no results" message,
  // and anything else with nothing to offer (an empty term, a hidden addition) closes it.
  renderMessage(term: string, maxReached: boolean | number | null) {
    this.menu.querySelector(':scope > .message')?.remove()
    if (this.selectableItems().length) return

    let message: string | null = null
    if (maxReached) {
      message = this.settings.message.maxSelections.replace('{maxCount}', String(this.settings.maxSelections))
    } else if (term && !this.settings.allowAdditions) {
      message = this.settings.message.noResults
    }

    if (message) {
      this.menu.insertAdjacentHTML('beforeend', `<div class="message">${escapeHTML(message)}</div>`)
    } else {
      this.hide({ clearSearch: false })
    }
  }

  hasContent() {
    return this.selectableItems().length > 0 || !!this.menu.querySelector(':scope > .message')
  }

  selectableItems() {
    return this.items().filter(item => !item.matches('.filtered, .hidden, .disabled'))
  }

  highlight(item: HTMLElement | null | undefined) {
    this.menu.querySelectorAll('.item.selected').forEach(selected => selected.classList.remove('selected'))
    if (!item) return

    item.classList.add('selected')
    this.scrollMenuTo(item)
  }

  // Scrolls the menu only, never the page.
  scrollMenuTo(item: HTMLElement) {
    const { menu } = this
    if (item.offsetTop < menu.scrollTop) {
      menu.scrollTop = item.offsetTop
    } else if (item.offsetTop + item.offsetHeight > menu.scrollTop + menu.clientHeight) {
      menu.scrollTop = item.offsetTop + item.offsetHeight - menu.clientHeight
    }
  }

  // Like Fomantic's selectOnKeydown: the arrows pick the item of a select right away, as a
  // native <select> does. With a search, they pick the item Enter or leaving the field will
  // commit, rather than the text typed so far.
  chooseWithKeyboard(step: number) {
    this.moveHighlight(step)
    this.keyboardChosen = true
    const highlighted = this.menu.querySelector<HTMLElement>('.item.selected')
    if (highlighted && !this.search && !this.isMultiple) this.selectItem(highlighted)
  }

  // The item picked with the arrows since the last keystroke in the search, if any.
  keyboardChoice() {
    return this.keyboardChosen ? this.menu.querySelector<HTMLElement>('.item.selected') : null
  }

  moveHighlight(step: number) {
    const items = this.selectableItems()
    if (!items.length) return

    const index = items.indexOf(this.menu.querySelector('.item.selected') as HTMLElement)
    this.highlight(items[(index + step + items.length) % items.length])
  }

  // Remote search: `{query}` in the url is the search term, and the answer lists
  // `{ name, value, text }` results, `type: 'header'` for a section title.
  queryRemote(term: string) {
    clearTimeout(this.remoteTimer)
    this.remoteTimer = setTimeout(() => this.fetchResults(term), term ? 50 : 0)
  }

  async fetchResults(term: string) {
    const request = (this.lastRequest = (this.lastRequest || 0) + 1)
    const { url, className } = this.settings.apiSettings!
    const loadingClass = className?.loading || 'loading'
    this.module.classList.add(loadingClass)

    let results: DropdownResult[] = []
    try {
      const response = await fetch(url.replace('{query}', encodeURIComponent(term)), {
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }
      })
      if (response.ok) results = ((await response.json()) as { results?: DropdownResult[] }).results || []
    } catch {
      // A failed search leaves the menu empty, like an answer without results.
    }
    if (request !== this.lastRequest || !this.module.isConnected) return

    this.module.classList.remove(loadingClass)
    this.renderResults(results)
    if (!this.isVisible() && document.activeElement === this.search && this.hasContent()) this.show({ query: false })
  }

  renderResults(results: DropdownResult[]) {
    const values = this.values()
    const content = (value: string | undefined) =>
      this.settings.preserveHTML ? String(value ?? '') : escapeHTML(value ?? '')

    this.menu.innerHTML = results
      .map(result => {
        if (result.type === 'header') return `<div class="header">${content(result.name)}</div>`

        const element = document.createElement('div')
        element.className = result.disabled ? 'disabled item' : 'item'
        element.dataset.value = result.value
        if (result.text !== undefined) element.dataset.text = result.text
        if (result.edit_url) element.dataset.editUrl = result.edit_url
        element.innerHTML = content(result.name)
        return element.outerHTML
      })
      .join('')

    this.items().forEach(item => {
      const active = values.includes(this.itemValue(item))
      item.classList.toggle('active', active)
      item.classList.toggle('filtered', active && this.isMultiple)
    })
    this.renderMessage(this.search?.value.trim() || '', this.maxReached())
    this.highlight(this.selectableItems()[0])
  }

  // Events ------------------------------------------------------------------------------

  bind() {
    this.module.addEventListener('mousedown', () => (this.mouseDown = true))
    this.module.addEventListener('click', event => this.onClick(event))
    this.module.addEventListener('focusout', event => this.onFocusOut(event))
    this.module.addEventListener('keydown', event => this.onKeydown(event))
    this.menu.addEventListener('mousedown', () => (this.menuMouseDown = true))
    this.menu.addEventListener('click', event => this.onMenuClick(event))
    // A focused search opens the menu, from the keyboard or the mouse: its click only shows it
    // again. The mouse is left to the click on the other dropdowns, whose click toggles.
    ;(this.search || this.module).addEventListener('focus', () => {
      if (this.settings.showOnFocus && this.isSelection && (this.search || !this.mouseDown)) this.show()
    })
    this.search?.addEventListener('input', () => this.onSearchInput())
    // A script sets the value of the input, then dispatches `change` on it.
    this.input?.addEventListener('change', () => {
      if (!this.dispatchingChange) this.refresh()
    })
  }

  bindDocument() {
    this.unbindDocument()
    this.documentClick = event => {
      if (!this.module.isConnected) {
        this.unbindDocument()
      } else if (!this.module.contains(event.target as Node | null)) {
        this.hide()
      }
    }
    this.documentMouseUp = () => {
      this.mouseDown = false
      this.menuMouseDown = false
    }
    document.addEventListener('click', this.documentClick, true)
    document.addEventListener('mouseup', this.documentMouseUp)
  }

  unbindDocument() {
    document.removeEventListener('click', this.documentClick!, true)
    document.removeEventListener('mouseup', this.documentMouseUp!)
  }

  onClick(event: MouseEvent) {
    this.mouseDown = false
    if (this.isDisabled() || this.menu.contains(event.target as Node | null)) return

    const label = (event.target as Element).closest<HTMLElement>('a.label')
    if (label && this.module.contains(label)) {
      if ((event.target as Element).closest('.delete.icon')) this.removeValue(label.dataset.value!)
      return
    }

    if (this.search && (event.target === this.search || this.isMultiple)) {
      this.show()
    } else {
      this.toggle()
    }
    if (this.isVisible() && this.search && document.activeElement !== this.search) this.search.focus()
  }

  onMenuClick(event: MouseEvent) {
    this.menuMouseDown = false
    const item = (event.target as Element).closest<HTMLElement>('.item')
    if (!item || !this.menu.contains(item) || item.classList.contains('disabled')) return
    if (this.settings.action === 'nothing') return

    // An item without a value in a dropdown that has one (a link in a menu, a locked
    // option) is not a choice.
    const choice = this.select || (this.input && item.dataset.value !== undefined)
    if (this.settings.action === 'activate' && choice) this.selectItem(item)
    if (!this.isMultiple) this.hide()
  }

  selectItem(item: HTMLElement) {
    this.setSelected(this.itemValue(item), this.itemText(item))
    if (this.isMultiple && this.search) {
      this.clearSearch()
      this.filter('')
      this.search.focus()
    }
  }

  onFocusOut(event: FocusEvent) {
    if (this.module.contains(event.relatedTarget as Node | null) || this.menuMouseDown || this.mouseDown) return

    const highlighted = this.menu.querySelector<HTMLElement>('.item.selected')
    const forced = this.settings.forceSelection && this.search?.value.trim()
    if (!this.isMultiple && highlighted && (forced || this.keyboardChoice())) this.selectItem(highlighted)
    if (this.isMultiple && this.search) this.clearSearch()
    this.hide()
  }

  onSearchInput() {
    this.keyboardChosen = false
    const term = this.search!.value
    this.text?.classList.toggle('filtered', term !== '')

    if (this.settings.apiSettings) {
      this.queryRemote(term)
      return
    }

    this.filter(term)
    if (!this.isVisible() && this.hasContent()) this.show()
  }

  onKeydown(event: KeyboardEvent) {
    if (this.isDisabled() || !this.isSelection) return

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowUp':
        event.preventDefault()
        if (this.isVisible()) {
          this.chooseWithKeyboard(event.key === 'ArrowDown' ? 1 : -1)
        } else {
          this.show()
        }
        break
      case 'Enter': {
        event.preventDefault()
        const highlighted = this.menu.querySelector<HTMLElement>('.item.selected')
        if (this.isVisible() && highlighted) {
          this.selectItem(highlighted)
          if (!this.isMultiple) this.hide()
        } else {
          this.show()
        }
        break
      }
      case 'Escape':
        if (this.isVisible()) {
          event.preventDefault()
          this.hide()
        }
        break
      case 'Backspace':
        if (this.isMultiple && this.search?.value === '' && this.values().length) {
          this.removeValue(this.values().slice(-1)[0])
        }
        break
    }
  }
}
